import React, {useCallback, useEffect, useRef, useState} from "react";
import PropTypes from 'prop-types';
import {
    SESSION_HAS_EXPIRED,
    SESSION_TIMEOUT_MODAL_TIMEOUT_MS,
    SESSION_TIMEOUT_SHOW_MODAL_MS,
    useAuth,
} from "../../context/authContext";
import Header from "./Header";
import Sidebar from "./Sidebar";
import IdleTimeoutModal from "../../components/IdleModal";
import {useIdleTimer} from "react-idle-timer";
import axios from "../../utils/apiClient";
import {LocalStorageUtil, SessionStorageUtil} from "../../utils/localStorageUtil";
import PendoModalObserver from "./components/PendoModalObserver";
import PendoTooltipObserver from "./components/PendoTooltipObserver";
import {useLocation} from "react-router-dom";
import {TRACK_EVENTS, trackPendoEvent} from "../../utils/pendoTrack";

const CE_RESTORE_OBJ = "ce_restore_obj";
const RECENT_QUERY_SETTINGS_KEY = "ce-recent-query-settings-key";

const MainLayout = ({ children }) => {
  const auth = useAuth();
  const isAuthenticated = auth?.isAuthenticated();
  const location = useLocation();
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const [showIdleTimeoutModal, setshowIdleTimeoutModal] = useState(false);
  const [isExtendingSession, setIsExtendingSession] = useState(false);
  const isExtendingSessionRef = useRef(false);

  const handleOnIdle = useCallback(() => {
    setshowIdleTimeoutModal(true);
  }, []);

  const handleLogoutBody = useCallback((save) => {
    sessionStorage.setItem(SESSION_HAS_EXPIRED, true);
    setshowIdleTimeoutModal(false);
    if (save) {
      //save state for restore
      const recentKey = SessionStorageUtil.getObj(RECENT_QUERY_SETTINGS_KEY);
      const quickListObj = SessionStorageUtil.getObj(recentKey);
      LocalStorageUtil.setObj(CE_RESTORE_OBJ, {
        quickListObj: quickListObj,
        recentKey: recentKey,
        restoreUrl: window.location.pathname + window.location.search
      });
    }
  }, []);

  const handleLogout = useCallback((save) => {
    try {
      handleLogoutBody(save);
    } catch (e) {
      console.error("Logout body failed:", e);
    } finally {
      auth.logout(save ? "timeout" : "manual");
    }
  }, [auth, handleLogoutBody]);

  const {
    reset
  } = useIdleTimer({
    timeout: SESSION_TIMEOUT_SHOW_MODAL_MS,
    onIdle: handleOnIdle,
    stopOnIdle: true,
  });

  const handleExtendSession = useCallback(async () => {
    if (isExtendingSessionRef.current) {
      return;
    }

    isExtendingSessionRef.current = true;
    setIsExtendingSession(true);
    reset();
    try {
      await axios.put(
        process.env.REACT_APP_REST_API_BASE_URL +
        `/v1/session/reset`
      );
      setshowIdleTimeoutModal(false);
    } catch (e) {
      console.error("Session reset failed:", e);
      handleLogout(true);
    } finally {
      isExtendingSessionRef.current = false;
      setIsExtendingSession(false);
    }
  }, [handleLogout, reset]);

  useEffect(() => {
    const route = location?.pathname || window?.location?.pathname || "/";
    const routeLower = route.toLowerCase();

    if (routeLower.includes("patientprofile")) {
      trackPendoEvent(TRACK_EVENTS.NAV_PATIENT_PROFILE_OPEN, {
        route,
        tab: location?.hash ? location.hash.replace("#", "") : "overview",
        source_module: "MainLayout"
      });
    }
  }, [location]);

  return (
    <div className="main-wrapper w-100">
      {isAuthenticated ? (
        <>
          <PendoModalObserver />
          <PendoTooltipObserver />
          <div className="d-flex justify-content-start">
            <Sidebar sidebarExpanded={sidebarExpanded} setSidebarExpanded={setSidebarExpanded} />
            <div className="page-wrapper d-flex flex-column overflow-y-auto p-0 vh-100 w-100">
              <Header sidebarExpanded={sidebarExpanded} setSidebarExpanded={setSidebarExpanded} />
              <div className="info-wrapper">
                {children}
              </div>
            </div>
          </div>
        </>
      ) : (
        children
      )}
      <IdleTimeoutModal
        showModal={showIdleTimeoutModal}
        handleExtendSession={handleExtendSession}
        handleLogout={handleLogout}
        isExtendingSession={isExtendingSession}
        logoutAfterMS={SESSION_TIMEOUT_MODAL_TIMEOUT_MS}
      />
    </div>
  );
};


MainLayout.propTypes = {
  children: PropTypes.node.isRequired,
};

export default MainLayout;
export {CE_RESTORE_OBJ};
